/**
 * Property Video Player Component
 * Feature: Property Video Generation
 *
 * Displays the generated final property video with playback controls and download.
 */

import { memo, useState, useCallback, useRef } from 'react';
import { useQuery } from 'convex/react';
import { Play, Pause, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '../../../convex/_generated/api';
import type { IPropertyVideoPlayerProps } from '@/@types/property';

/**
 * Format bytes to human readable size
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format duration in seconds to display string
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
}

/**
 * Video Player Component
 */
function VideoPlayer({ videoUrl }: { videoUrl: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  return (
    <div className="relative aspect-[9/16] w-full max-w-[320px] overflow-hidden rounded-lg bg-gray-900">
      <video
        ref={videoRef}
        src={videoUrl}
        className="h-full w-full object-cover"
        onEnded={handleEnded}
        playsInline
      />
      <button
        type="button"
        onClick={togglePlay}
        className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity hover:opacity-100"
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
      >
        <div className="rounded-full bg-white/90 p-3">
          {isPlaying ? (
            <Pause className="h-6 w-6 text-gray-900" />
          ) : (
            <Play className="h-6 w-6 text-gray-900" />
          )}
        </div>
      </button>
    </div>
  );
}

/**
 * Sanitize filename for download
 */
function sanitizeFilename(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);
}

/**
 * PropertyVideoPlayer Component
 */
function PropertyVideoPlayerComponent({
  finalVideoId,
  durationSeconds,
  fileSizeBytes,
  propertyId,
  propertyTitle,
}: IPropertyVideoPlayerProps) {
  const videoUrl = useQuery(api.storage.getUrl, { storageId: finalVideoId });
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!videoUrl || isDownloading) return;

    const filename = propertyTitle
      ? `property-${sanitizeFilename(propertyTitle)}.mp4`
      : `property-${propertyId}.mp4`;

    setIsDownloading(true);
    try {
      // Fetch the video as a blob to handle cross-origin downloads
      const response = await fetch(videoUrl);
      const blob = await response.blob();

      // Create a blob URL and trigger download
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download video:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [videoUrl, propertyId, propertyTitle, isDownloading]);

  // Loading state
  if (videoUrl === undefined) {
    return (
      <div className="flex flex-col items-center space-y-3">
        <div className="relative aspect-[9/16] w-full max-w-[320px] overflow-hidden rounded-lg bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  // No URL available
  if (!videoUrl) {
    return (
      <div className="flex flex-col items-center space-y-3">
        <div className="relative aspect-[9/16] w-full max-w-[320px] overflow-hidden rounded-lg bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
          <p className="text-sm text-gray-500">Video unavailable</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-3">
      <VideoPlayer videoUrl={videoUrl} />

      {(durationSeconds !== undefined || fileSizeBytes !== undefined) && (
        <div className="flex w-full max-w-[320px] items-center justify-between text-xs text-gray-600 dark:text-gray-400">
          <span>{durationSeconds !== undefined ? formatDuration(durationSeconds) : '-'}</span>
          <span>{fileSizeBytes !== undefined ? formatFileSize(fileSizeBytes) : '-'}</span>
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        className="w-full max-w-[320px] gap-2"
        onClick={handleDownload}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Downloading...
          </>
        ) : (
          <>
            <Download className="h-4 w-4" />
            Download Video
          </>
        )}
      </Button>
    </div>
  );
}

const PropertyVideoPlayer = memo(PropertyVideoPlayerComponent);

export { PropertyVideoPlayer };
